import type { ComponentProps } from 'react';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@vs-shop/ui/carousel';
import { clsxm, type ClassValue } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';
import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * A row of articles a page steps through sideways — what else to read after
 * this one, what a product's shoppers read first. Flat named parts a tenant
 * arranges in its own JSX, each painting itself.
 *
 *   <ArticleCarousel>
 *     <ArticleCarouselHeader>
 *       <ArticleCarouselTitle>{title}</ArticleCarouselTitle>
 *       <ArticleCarouselSteps>
 *         <ArticleCarouselPrevious />
 *         <ArticleCarouselNext />
 *       </ArticleCarouselSteps>
 *     </ArticleCarouselHeader>
 *
 *     <ArticleCarouselTrack>
 *       {articles.map((article) => (
 *         <ArticleCarouselItem key={article.slug}>{card(article)}</ArticleCarouselItem>
 *       ))}
 *     </ArticleCarouselTrack>
 *   </ArticleCarousel>
 *
 * What travels in a place is whatever the page composes there — rcprofi puts
 * `@vs-shop/commerce-ui/articleCard` in its stacked arrangement in each, which
 * is why nothing here knows what an article is.
 *
 * The rail is deliberately NOT `@vs-shop/commerce-ui/productRail`, though both
 * step a row of cards sideways: a product rail clips its places and hangs its
 * controls outside the viewport, this one lays its two controls beside the
 * title and lets the places run to the page's edge. They share a shape at the
 * level of a sketch and no recipe below it.
 *
 * **The title is not a heading**, for the reason every section title in this
 * tier is not one; a page that wants it in its outline hands the element in
 * through `render`.
 *
 * The header is outside the moving part and the two controls are inside it:
 * the primitive's context begins at {@link ArticleCarousel}, so both halves
 * are under it, but only the controls read it.
 */

/** The rail: a title over a row of places. */
const articleCarouselClasses = 'grid gap-6 lap:gap-8';

export type ArticleCarouselProps = Omit<ComponentProps<'div'>, 'className'> & {
  readonly className?: ClassValue;
};

/** The rail. */
export const ArticleCarousel = ({ className, ...props }: ArticleCarouselProps) => (
  <Carousel
    data-slot="article-carousel"
    className={clsxm(articleCarouselClasses, className)}
    {...props}
  />
);

/** The title on one side, the way through the row on the other. */
const articleCarouselHeaderClasses = 'flex justify-between';

export type ArticleCarouselHeaderProps = ComponentProps<'div'>;

/** The rail's header. */
export const ArticleCarouselHeader = ({ className, ...props }: ArticleCarouselHeaderProps) => (
  <div
    data-slot="article-carousel-header"
    className={clsxm(articleCarouselHeaderClasses, className)}
    {...props}
  />
);

/** What the row is a run of. A neutral element, for the reason a card's headline is one. */
const articleCarouselTitleClasses =
  'font-heading text-foreground inline tracking-tight lap:text-[32px]/[38px] text-[26px]/[34px] font-medium';

export type ArticleCarouselTitleProps = ComponentProps<'div'> & {
  /** The element to paint instead of the neutral box — a page's own heading. */
  readonly render?: RenderElement;
};

/** The rail's title. */
export const ArticleCarouselTitle = ({
  className,
  render,
  ...props
}: ArticleCarouselTitleProps) => (
  <RenderedPart
    dataSlot="article-carousel-title"
    className={clsxm(articleCarouselTitleClasses, className)}
    render={render}
    {...props}
  />
);

/** The pair of controls, at the header's far end. */
const articleCarouselStepsClasses = 'flex gap-4';

export type ArticleCarouselStepsProps = ComponentProps<'div'>;

/** The rail's controls. */
export const ArticleCarouselSteps = ({ className, ...props }: ArticleCarouselStepsProps) => (
  <div
    data-slot="article-carousel-steps"
    className={clsxm(articleCarouselStepsClasses, className)}
    {...props}
  />
);

export type ArticleCarouselStepProps = Omit<
  ComponentProps<typeof CarouselPrevious>,
  'position' | 'size' | 'navSize'
>;

/**
 * A step back through the row.
 *
 * The primitive's `position="free"` and `size="icon"` are the rail's answer
 * rather than a caller's: the controls sit in the header this section drew, not
 * over the places, and a rail whose two controls disagreed about their size
 * would be a caller typing the same pair twice.
 */
export const ArticleCarouselPrevious = ({ className, ...props }: ArticleCarouselStepProps) => (
  <CarouselPrevious
    navSize="base"
    data-slot="article-carousel-previous"
    position="free"
    size="icon"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/** A step onward through the row. */
export const ArticleCarouselNext = ({ className, ...props }: ArticleCarouselStepProps) => (
  <CarouselNext
    navSize="base"
    data-slot="article-carousel-next"
    position="free"
    size="icon"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

export type ArticleCarouselTrackProps = ComponentProps<typeof CarouselContent>;

/** The row the places travel in. */
export const ArticleCarouselTrack = ({ className, ...props }: ArticleCarouselTrackProps) => (
  <CarouselContent
    data-slot="article-carousel-track"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/**
 * One article's place.
 *
 * A fixed measure rather than a share of the row: a rail of article cards is
 * read by how many fit, and a place that grew with the page would show three
 * cards on every screen at three different sizes.
 */
const articleCarouselItemClasses = 'w-[320px] basis-auto tab:w-[400px] tab:pl-6';

export type ArticleCarouselItemProps = ComponentProps<typeof CarouselItem>;

/** An article's place. */
export const ArticleCarouselItem = ({ className, ...props }: ArticleCarouselItemProps) => (
  <CarouselItem
    data-slot="article-carousel-item"
    className={clsxm(articleCarouselItemClasses, className)}
    {...props}
  />
);
